import type { DeviceProfile } from "@squire/core";
import { storage } from "../system/storage.js";

/** Per-device settings (SPEC 9): stored locally, never on the server. */
export interface DeviceSettings {
  id: string;
  name: string;
  profile: DeviceProfile;
  serverUrl: string;
  token: string;
  voiceOn: boolean;
  voiceName: string;
  cameraOn: boolean;
  nightDim: boolean;
  /**
   * Opt-in: speech recognition by Whisper on the owner's Cloudflare Worker (for devices without
   * on-device Polish). The utterance leaves the device for transcription only, never stored.
   */
  cloudStt: boolean;
  /** Debug mode: chat commands (/pomoc) drive animations, places and game state. */
  debug: boolean;
  /** Run without a server (demo / offline): RoomCore + mock brain in the browser. */
  local: boolean;
}

const KEY = "squire-device";

function guessName(): string {
  const big = Math.min(screen.width, screen.height) >= 600;
  return big ? "tablet" : "telefon";
}

export function loadDevice(): DeviceSettings {
  const defaults: DeviceSettings = {
    id: "",
    name: guessName(),
    profile: Math.min(screen.width, screen.height) >= 600 ? "wall" : "pocket",
    serverUrl: import.meta.env.VITE_SERVER_URL ?? "",
    token: "",
    voiceOn: true,
    voiceName: "",
    cameraOn: false,
    cloudStt: false,
    debug: false,
    nightDim: true,
    local: false,
  };
  const d = storage.json<DeviceSettings>(KEY, defaults);
  if (!d.id) d.id = `dev-${crypto.randomUUID().slice(0, 12)}`;
  if (!d.serverUrl && import.meta.env.VITE_SERVER_URL) d.serverUrl = import.meta.env.VITE_SERVER_URL;
  if (import.meta.env.DEV && !d.token && import.meta.env.VITE_DEV_TOKEN)
    d.token = import.meta.env.VITE_DEV_TOKEN;
  // Token from a QR code / link: ?token=... is stored and removed from the address bar.
  const url = new URL(location.href);
  const token = url.searchParams.get("token");
  const server = url.searchParams.get("server");
  if (token) d.token = token;
  if (server) d.serverUrl = server;
  if (url.searchParams.get("local") === "1") d.local = true;
  if (token || server) {
    url.searchParams.delete("token");
    url.searchParams.delete("server");
    history.replaceState(null, "", url.toString());
  }
  saveDevice(d);
  return d;
}

export function saveDevice(d: DeviceSettings): void {
  storage.set(KEY, JSON.stringify(d));
}
